"use client"

import type React from "react"
import { useEffect, useState } from "react"
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  FormHelperText,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
} from "@mui/material"
import { Close } from "@mui/icons-material"
import Swal from "sweetalert2"
import "../../styles/styles.css"
import { useAuth } from "../../context/AuthContext"
import { useProperty } from "../../context/PropertyContext"
import { createPurchaseRequest } from "../../services/requestService"
import { useNavigate } from "react-router-dom"

interface PurchaseModalProps {
  open: boolean
  onClose: () => void
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({ open, onClose }) => {
  const { userToken } = useAuth()
  const { selectedProperty } = useProperty()
  const [formData, setFormData] = useState({
    fullName: "",
    profession: "",
    economicActivity: "",
    paymentMethod: "",
    availableFunds: "",
    financingNeeded: false,
    message: "",
  })
  const [errors, setErrors] = useState<Partial<Record<keyof typeof formData, string>>>({})
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (userToken) {
      const fullName = userToken.user.fullName
      const position = userToken.user.position

      setFormData((prevState) => ({
        ...prevState,
        fullName: fullName || "",
        profession: position || "",
      }))
    }
  }, [userToken])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    const numericValue = Number(value)
    if (name === "availableFunds") {
      if (numericValue < 0) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "La cantidad no puede ser negativa.",
        }))
        return
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "",
        }))
      }
    }

    setFormData({ ...formData, [name]: value })
  }

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, paymentMethod: e.target.value })
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, financingNeeded: e.target.checked })
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof typeof formData, string>> = {}

    if (!formData.fullName.trim()) {
      newErrors.fullName = "El campo nombre completo es obligatorio."
    }
    if (!formData.profession.trim()) {
      newErrors.profession = "El campo profesión u ocupación es obligatorio."
    }
    if (!formData.economicActivity.trim()) {
      newErrors.economicActivity = "El campo actividad económica o lugar de trabajo es obligatorio."
    }
    if (!formData.paymentMethod.trim()) {
      newErrors.paymentMethod = "Seleccione un método de pago."
    }
    if (
      !formData.availableFunds.trim() ||
      isNaN(Number(formData.availableFunds)) ||
      Number(formData.availableFunds) < 0
    ) {
      newErrors.availableFunds = "Introduzca un monto válido para los fondos disponibles."
    }

    if (!formData.message.trim()) {
      newErrors.message = "El campo mensaje es obligatorio."
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    setLoading(true)

    try {
      const userProfile = userToken!.user.profiles.find((profile) => profile.roleName === "Cliente")

      if (!userProfile) {
        console.error("Perfil de cliente no encontrado:", userToken!.user.profiles)
        throw new Error("Perfil de Cliente no encontrado.")
      }

      const formDataToSend = new FormData()
      formDataToSend.append("UserProfileId", userProfile.id.toString())
      formDataToSend.append("PropertyId", selectedProperty!.id_Property.toString())
      formDataToSend.append("ModeId", "1")
      formDataToSend.append("EconomicActivity", formData.economicActivity)
      formDataToSend.append("Message", formData.message)
      formDataToSend.append("ModuleStateId", "8")
      formDataToSend.append("PaymentMethod", formData.paymentMethod)
      formDataToSend.append("AvailableFunds", formData.availableFunds)
      formDataToSend.append("FinancingNeeded", formData.financingNeeded ? "true" : "false")

      const result = await createPurchaseRequest(formDataToSend)


      // Check if the request was successful
      if (typeof result !== "string" || !result.includes("se creó correctamente")) {
        throw new Error("Error al enviar la solicitud de compra")
      }

      Swal.fire({
        title: "¡Formulario enviado!",
        text: "Gracias por completar el formulario. Se ha creado una conversación con el agente inmobiliario.",
        icon: "success",
        confirmButtonText: "Ver mensajes",
        showCancelButton: true,
        cancelButtonText: "Cerrar",
        customClass: {
          container: "custom-popup",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          // Navigate to messages page
          navigate("/properties/messages")
        }
      })

      handleClose()
    } catch (error) {
      console.error("Ocurrió un error al enviar la solicitud:", error)
      Swal.fire({
        title: "Error",
        text: "Ocurrió un error al enviar el formulario. Por favor, intenta nuevamente.",
        icon: "error",
        confirmButtonText: "Aceptar",
        customClass: {
          container: "custom-popup",
        },
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({
      fullName: "",
      profession: "",
      economicActivity: "",
      paymentMethod: "",
      availableFunds: "",
      financingNeeded: false,
      message: "",
    })
    setErrors({})
    onClose()
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "60%", md: "35%" },
          maxHeight: "80vh",
          bgcolor: "background.paper",
          p: 3,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#aaa",
          },
        }}
      >
        <Typography variant="h6" textAlign="center">
          {" "}
          {`¡Hola ${userToken!.user.fullName}! Completa este formulario para ayudarte en el proceso de compra de la propiedad.`}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", top: 0, right: 0 }}>
          <Close />
        </IconButton>

        <Divider />

        {/* Nombre completo */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <label htmlFor="fullName">Nombre completo</label>
          <input
            id="fullName"
            name="fullName"
            type="text"
            value={formData.fullName}
            onChange={handleInputChange}
            placeholder="Escribe tu nombre completo"
            className="inputField"
            readOnly
          />
          {errors.fullName && <FormHelperText error>{errors.fullName}</FormHelperText>}
        </Box>

        {/* Profesión u ocupación */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <label htmlFor="profession">Profesión u ocupación</label>
          <input
            id="profession"
            name="profession"
            type="text"
            value={formData.profession}
            onChange={handleInputChange}
            placeholder="Escribe tu profesión u ocupación"
            className="inputField"
            readOnly
          />
          {errors.profession && <FormHelperText error>{errors.profession}</FormHelperText>}
        </Box>

        {/* Actividad económica o lugar de trabajo */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <label htmlFor="economicActivity">Actividad económica o lugar de trabajo</label>
          <input
            id="economicActivity"
            name="economicActivity"
            type="text"
            value={formData.economicActivity}
            onChange={handleInputChange}
            placeholder="Escribe tu actividad económica o lugar de trabajo"
            className="inputField"
          />
          {errors.economicActivity && <FormHelperText error>{errors.economicActivity}</FormHelperText>}
        </Box>

        {/* Fondos disponibles */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <label htmlFor="availableFunds">¿Cuánto dinero dispone para la compra?</label>
          <input
            id="availableFunds"
            name="availableFunds"
            type="number"
            value={formData.availableFunds}
            onChange={handleInputChange}
            placeholder="Escribe el monto disponible"
            className="inputField"
          />
          {errors.availableFunds && <FormHelperText error>{errors.availableFunds}</FormHelperText>}
        </Box>

        {/* Método de pago */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle1">¿Cómo realizará la compra?</Typography>
          <RadioGroup name="paymentMethod" value={formData.paymentMethod} onChange={handleRadioChange}>
            <FormControlLabel
              value="Crédito"
              control={
                <Radio
                  sx={{
                    "&.Mui-checked": {
                      color: "green",
                    },
                  }}
                />
              }
              label="Crédito"
              className="customCheckbox"
            />
            <FormControlLabel
              value="Efectivo"
              control={
                <Radio
                  sx={{
                    "&.Mui-checked": {
                      color: "green",
                    },
                  }}
                />
              }
              label="Efectivo"
              className="customCheckbox"
            />
          </RadioGroup>
          {errors.paymentMethod && <FormHelperText error>{errors.paymentMethod}</FormHelperText>}
        </Box>

        {/* Necesita financiamiento */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.financingNeeded}
                onChange={handleCheckboxChange}
                name="financingNeed"
                className="customCheckbox"
              />
            }
            label={
              <Typography variant="subtitle1" component="span">
                ¿Necesita financiamiento?
              </Typography>
            }
          />
        </Box>

        {/* Mensaje */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <label htmlFor="message">Mensaje</label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            placeholder="Escribe tu mensaje aquí"
            style={{
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              minHeight: "100px",
            }}
          />
          {errors.message && <FormHelperText error>{errors.message}</FormHelperText>}
        </Box>

        <Button variant="contained" color="success" onClick={handleSubmit} disabled={loading} sx={{ mt: 2 }}>
          {loading ? <CircularProgress size={24} color="success" /> : "Enviar"}
        </Button>
      </Box>
    </Modal>
  )
}

export default PurchaseModal
